import { Fragment, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetContactTransactions,
  useListMemberCharges,
  getGetContactTransactionsQueryKey,
  getListMemberChargesQueryKey,
  type ContactTransactions,
  type ContactTransactionRow,
  type MemberCharge,
} from "@workspace/api-client-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { isChargeRowCollectible, isStockOutRowCollectible } from "@/lib/charge-collection";
import { ChargeRecordPaymentDialog } from "@/components/billing/charge-record-payment-dialog";

// Phase 39 — shared "this account's Pending charges" fetch shape. Every call
// site (this panel + member-collect-charges.tsx) MUST use this EXACT param
// object (and the matching getListMemberChargesQueryKey) so React Query
// dedupes into ONE cache entry / network hit (39-RESEARCH.md Pitfall 4).
// pageSize is capped at 200 by the API (an account with >200 Pending
// non-Plan charges is effectively impossible, but the limit is real).
export function pendingChargesParams(accountId: number) {
  return { memberId: accountId, status: "Pending" as const, pageSize: 200 };
}

/** INR formatter — mirrors account-detail.tsx's formatINR. */
function formatINR(amount: number | null | undefined): string {
  const n = Number(amount ?? 0);
  if (!Number.isFinite(n)) return "—";
  return `₹${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
  }).format(n)}`;
}

function fmtDate(d: string | Date | null | undefined): string {
  if (!d) return "—";
  return format(new Date(d), "MMM d, yyyy");
}

/** Epoch ms for sorting; null/undefined dates sort last. */
function toTime(d: string | Date | null | undefined): number {
  return d ? new Date(d).getTime() : 0;
}

// Bucket order + labels — EXACT match to account-detail.tsx's CATEGORY_LABELS
// order (minus Miscellaneous, which has no counterpart on ContactTransactions).
// Do not reorder or re-derive this list elsewhere.
//
// Phase 40 (40-07) — "accountLevel" appended last: the FIX-2 billing-contact
// rollup bucket (still-unattributed account-level Pending non-Plan
// member_charges, source: "member_charge"). Only ever non-empty on the
// account's single designated Billing contact's profile; every row in it
// gets the "Account-level" badge below (row.accountLevel === true).
const BUCKETS: { key: keyof ContactTransactions; label: string }[] = [
  { key: "plan", label: "Plan" },
  { key: "booking", label: "Booking" },
  { key: "conferenceRoom", label: "Conference Room" },
  { key: "addOns", label: "Add-ons" },
  { key: "fnb", label: "F&B" },
  { key: "prints", label: "Prints" },
  { key: "stationery", label: "Stationery" },
  { key: "accountLevel", label: "Account-level" },
];

type FlatTransactionRow = ContactTransactionRow & {
  category: string;
  bucket: keyof ContactTransactions;
};

/**
 * Member detail page — per-member Transactions panel.
 *
 * Flattens `useGetContactTransactions(contactId)`'s 8 category buckets into a
 * SINGLE chronological (newest-first) list — no per-category section headers.
 * The category (Plan / Booking / Conference Room / Add-ons / F&B / Prints /
 * Stationery / Account-level — from account-detail.tsx's CATEGORY_LABELS
 * taxonomy, plus Phase 40's rollup bucket) is shown as a muted subtext under
 * each row's description instead. Empty categories simply don't contribute
 * rows.
 *
 * Phase 39 — adds an Action column: any row that's collectible via EITHER
 * rail exposes a per-row "Record payment" button wired to the shared
 * ChargeRecordPaymentDialog (SC3). Plan rows (memberChargeId null, source
 * plan_period) and non-Pending charge rows get an em-dash, never an action —
 * see 39-RESEARCH.md Pitfall 2 (never mirror account-detail.tsx's looser
 * `status !== "Waived"` gate here).
 *
 * Plan 09 (gap closure) — a row routes to EXACTLY ONE rail:
 * `isChargeRowCollectible` (memberChargeId != null → opens the dialog with
 * `charge`) OR `isStockOutRowCollectible` (charge-less, unpaid stock_out row
 * → opens the SAME dialog with `stockOutTargets: [{ id: row.id, ... }]`, the
 * row's own id IS the stock_out id). The two predicates are mutually
 * exclusive by construction (39-07), so a row never triggers both dialogs.
 *
 * Phase 40 (40-07) — the new `accountLevel` bucket (FIX-2 billing-contact
 * rollup: still-unattributed account-level Pending non-Plan `member_charges`,
 * `source: "member_charge"`) is rendered in the SAME flat list — no separate
 * section. Every row with `accountLevel === true` gets a small "Account-level"
 * Badge next to its category subtext, so the viewing contact (always the
 * account's designated Billing contact — the only profile this bucket is ever
 * non-empty on) can tell it apart from their own consumption. It is
 * collectible via the existing charge rail (`isChargeRowCollectible`,
 * `memberChargeId` = the row's own `member_charges.id`) — settling it there
 * updates the SAME row the account page acts on, so it disappears from both
 * (no double-collect, SC5).
 */
export function MemberTransactionsPanel({
  contactId,
  accountId,
}: {
  contactId: number;
  accountId: number;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useGetContactTransactions(contactId);

  // This account's Pending non-Plan charges, fetched ONCE — reused to look up
  // the full MemberCharge object behind each collectible row's memberChargeId
  // (the transactions row itself only carries the id, not the charge's
  // description/total/etc. that ChargeRecordPaymentDialog needs).
  const pendingParams = pendingChargesParams(accountId);
  const { data: pendingChargesData } = useListMemberCharges(pendingParams, {
    query: { queryKey: getListMemberChargesQueryKey(pendingParams) },
  });
  const pendingChargeById = new Map<number, MemberCharge>(
    (pendingChargesData?.items ?? []).map((c) => [c.id, c]),
  );

  const [payCharge, setPayCharge] = useState<MemberCharge | null>(null);
  // Plan 09 (gap closure) — the stock_out rail's per-row target. The row id
  // IS the stock_out id (source: "stock_out"), so no extra lookup is needed
  // (unlike payCharge, which needs the full MemberCharge object).
  const [payStockOut, setPayStockOut] = useState<{
    id: number;
    description: string;
    total: number;
  } | null>(null);

  // 2026-07-15 — retro-bundle multi-select. Row keys are `${bucket}-${id}` to
  // match the TableRow keys below. When 2+ Pending collectible rows are
  // ticked, the action bar renders and opens ChargeRecordPaymentDialog with
  // the selection partitioned into: primary charge (first), additionalCharges
  // (rest), and stockOutTargets (charge-less stock-out rows).
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  // Per-cart Record-payment dialog target. Separate from the bulk-select
  // path so clicking a cart's own button doesn't clobber any multi-select
  // the operator has going.
  const [cartPayTarget, setCartPayTarget] = useState<{
    primary: MemberCharge | null;
    additional: MemberCharge[];
    stockOutTargets: { id: number; description: string; total: number }[];
  } | null>(null);
  // 2026-07-15 — expand/collapse per cart group so a "Cart of N items"
  // parent starts collapsed and the operator drills in on demand.
  const [expandedCarts, setExpandedCarts] = useState<Set<string>>(new Set());
  function toggleRowSelect(key: string) {
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  function toggleRowSelectMany(keys: string[], select: boolean) {
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      for (const k of keys) {
        if (select) next.add(k);
        else next.delete(k);
      }
      return next;
    });
  }
  function toggleCartExpand(cartId: string) {
    setExpandedCarts((prev) => {
      const next = new Set(prev);
      if (next.has(cartId)) next.delete(cartId);
      else next.add(cartId);
      return next;
    });
  }

  function handleDialogClose() {
    setPayCharge(null);
    queryClient.invalidateQueries({ queryKey: getGetContactTransactionsQueryKey(contactId) });
    queryClient.invalidateQueries({ queryKey: getListMemberChargesQueryKey(pendingParams) });
  }

  function handleStockOutClose() {
    setPayStockOut(null);
    queryClient.invalidateQueries({ queryKey: getGetContactTransactionsQueryKey(contactId) });
    queryClient.invalidateQueries({ queryKey: getListMemberChargesQueryKey(pendingParams) });
  }

  function handleBulkClose() {
    setBulkOpen(false);
    setSelectedRowKeys(new Set());
    queryClient.invalidateQueries({ queryKey: getGetContactTransactionsQueryKey(contactId) });
    queryClient.invalidateQueries({ queryKey: getListMemberChargesQueryKey(pendingParams) });
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-24 w-full rounded-md" />
        <Skeleton className="h-24 w-full rounded-md" />
      </div>
    );
  }

  const rows: FlatTransactionRow[] = BUCKETS.flatMap(({ key, label }) =>
    ((data?.[key] ?? []) as ContactTransactionRow[]).map((r) => ({
      ...r,
      bucket: key,
      // "accountLevel" is a provenance flag, not a category. Rollup rows carry
      // their real charge type from the server (sourceTypeToLabel — "Day Pass",
      // "Late Fee"); the badge below conveys the provenance, so keep the type.
      category: key === "accountLevel" ? (r.category ?? label) : label,
    })),
  ).sort((a, b) => toTime(b.date) - toTime(a.date));

  // 2026-07-15 — group stock_out rows that share a cart_id (multi-item
  // Pay-Per-Use submission) into a "Cart of N items" parent so the Member
  // profile reads consistently with the Account view. Cartless rows (and
  // non-stock_out rows) each render as their own item. A cart is a group
  // only when 2+ siblings share the id; a singleton keeps its normal row.
  type MemberDisplayItem =
    | { kind: "row"; row: FlatTransactionRow }
    | { kind: "cart"; cartId: string; rows: FlatTransactionRow[]; total: number; earliestDate: string | null | undefined; allPaid: boolean; consumer: string | null };
  const cartMap = new Map<string, FlatTransactionRow[]>();
  const nonCart: FlatTransactionRow[] = [];
  for (const r of rows) {
    const cid = r.source === "stock_out" && r.cartId ? r.cartId : null;
    if (cid) {
      const arr = cartMap.get(cid) ?? [];
      arr.push(r);
      cartMap.set(cid, arr);
    } else {
      nonCart.push(r);
    }
  }
  const displayItems: MemberDisplayItem[] = [];
  for (const [cartId, cartRows] of cartMap) {
    if (cartRows.length >= 2) {
      displayItems.push({
        kind: "cart",
        cartId,
        rows: cartRows,
        total: cartRows.reduce((s, r) => s + Number(r.amount ?? 0), 0),
        earliestDate: cartRows
          .map((r) => r.date)
          .filter((d): d is string => !!d)
          .sort()[0] ?? cartRows[0]!.date,
        allPaid: cartRows.every((r) => (r.status ?? "").toLowerCase() === "paid"),
        consumer: null,
      });
    } else {
      // Singleton cart — render as a normal row.
      for (const r of cartRows) displayItems.push({ kind: "row", row: r });
    }
  }
  for (const r of nonCart) displayItems.push({ kind: "row", row: r });
  displayItems.sort((a, b) => {
    const at = a.kind === "cart" ? toTime(a.earliestDate) : toTime(a.row.date);
    const bt = b.kind === "cart" ? toTime(b.earliestDate) : toTime(b.row.date);
    return bt - at;
  });

  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No transactions</p>;
  }

  // Partition current selection into the two rails so the bulk dialog can
  // consume them via the shared ChargeRecordPaymentDialog contract
  // (charge + additionalCharges + stockOutTargets).
  const selectedChargeIds: number[] = [];
  const selectedStockOutTargets: { id: number; description: string; total: number }[] = [];
  let selectedTotal = 0;
  for (const r of rows) {
    if (!selectedRowKeys.has(`${r.bucket}-${r.id}`)) continue;
    if (r.memberChargeId != null && isChargeRowCollectible(r)) {
      const c = pendingChargeById.get(r.memberChargeId);
      if (c) {
        selectedChargeIds.push(c.id);
        selectedTotal += Number(c.total);
      }
    } else if (isStockOutRowCollectible(r)) {
      selectedStockOutTargets.push({
        id: r.id,
        description: r.description,
        total: r.amount ?? 0,
      });
      selectedTotal += r.amount ?? 0;
    }
  }
  const selectedCharges = selectedChargeIds
    .map((id) => pendingChargeById.get(id))
    .filter((c): c is MemberCharge => c != null);
  const [primaryCharge, ...additionalCharges] = selectedCharges;
  const canBulk = selectedRowKeys.size >= 2;

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead>Date</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Receipt / Invoice #</TableHead>
            <TableHead>Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayItems.map((item) => {
            // ---------- Cart-of-N parent ----------
            // A cart is one atomic sale — payment covers ALL child items
            // together. So the Record-payment CTA + checkbox live on the
            // parent only; children below are display-only. This mirrors the
            // Account view's semantics (single Mark-as-Paid per cart).
            if (item.kind === "cart") {
              const { cartId, rows: cartRows, total, earliestDate, allPaid } = item;
              const isExpanded = expandedCarts.has(cartId);
              // Collectible stock-out targets for the cart (charge-less rail).
              // Every cart child is source: 'stock_out' by construction of
              // the group; a child with memberChargeId set would flow through
              // the charge rail, but the retro-bundle path already handles
              // that mixed case via additionalCharges when selected via bulk.
              const stockOutTargetsInCart = cartRows
                .filter(isStockOutRowCollectible)
                .map((r) => ({
                  id: r.id,
                  description: r.description,
                  total: r.amount ?? 0,
                }));
              const chargeTargetsInCart = cartRows
                .filter((r) => isChargeRowCollectible(r) && r.memberChargeId != null)
                .map((r) => pendingChargeById.get(r.memberChargeId!))
                .filter((c): c is MemberCharge => c != null);
              const cartHasCollectible =
                stockOutTargetsInCart.length + chargeTargetsInCart.length > 0;
              // Selection uses the CHILDREN's row keys so the existing bulk-
              // select partition logic (charge vs stock-out) works unchanged.
              // The parent checkbox is a convenience: ticking it selects all
              // collectible children; unticking clears them.
              const collectibleChildKeys = cartRows
                .filter((r) => isChargeRowCollectible(r) || isStockOutRowCollectible(r))
                .map((r) => `${r.bucket}-${r.id}`);
              const allChildrenSelected =
                collectibleChildKeys.length > 0 &&
                collectibleChildKeys.every((k) => selectedRowKeys.has(k));

              function openCartPayment() {
                // Route the same way the shared dialog does: first charge as
                // primary, rest as additional, stock_out rows as targets.
                const [primary, ...additional] = chargeTargetsInCart;
                setCartPayTarget({
                  primary: primary ?? null,
                  additional,
                  stockOutTargets: stockOutTargetsInCart,
                });
              }

              return (
                <Fragment key={`cart-${cartId}`}>
                  <TableRow className={allChildrenSelected ? "bg-primary/5" : undefined}>
                    <TableCell className="align-top">
                      {cartHasCollectible ? (
                        <input
                          type="checkbox"
                          checked={allChildrenSelected}
                          onChange={() =>
                            toggleRowSelectMany(collectibleChildKeys, !allChildrenSelected)
                          }
                          className="h-3.5 w-3.5 cursor-pointer accent-primary"
                          aria-label={
                            allChildrenSelected ? "Unselect cart" : "Select cart for bulk pay"
                          }
                        />
                      ) : null}
                    </TableCell>
                    <TableCell className="whitespace-nowrap align-top">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => toggleCartExpand(cartId)}
                          className="-ml-1 rounded p-0.5 hover:bg-muted"
                          aria-label={isExpanded ? "Collapse cart" : "Expand cart"}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5" />
                          )}
                        </button>
                        <span>{fmtDate(earliestDate)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      <div className="font-medium">Cart of {cartRows.length} items</div>
                      <div className="text-xs text-muted-foreground">{cartRows[0]!.category}</div>
                    </TableCell>
                    <TableCell className="align-top tabular-nums">{formatINR(total)}</TableCell>
                    <TableCell className="align-top capitalize">
                      {allPaid ? "Paid" : "Pending"}
                    </TableCell>
                    <TableCell className="align-top">—</TableCell>
                    <TableCell className="align-top">
                      {cartHasCollectible && !allChildrenSelected ? (
                        // Hide per-cart CTA once selected — the floating
                        // action bar's Collect button is the single action
                        // path while selection is active (avoids the "which
                        // button do I press?" confusion).
                        <Button size="sm" variant="outline" onClick={openCartPayment}>
                          Record payment
                        </Button>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                  {isExpanded
                    ? cartRows.map((r) => {
                        const rowKey = `${r.bucket}-${r.id}`;
                        // No individual checkbox / no individual CTA on child
                        // rows — payment is the cart's responsibility. Just
                        // the item breakdown so the operator can see what
                        // the parent line contains.
                        return (
                          <TableRow key={`cart-${cartId}-child-${rowKey}`} className="bg-muted/30">
                            <TableCell />
                            <TableCell />
                            <TableCell className="align-top pl-8 text-sm text-muted-foreground">
                              · {r.description}
                            </TableCell>
                            <TableCell className="align-top tabular-nums text-sm">
                              {formatINR(r.amount)}
                            </TableCell>
                            <TableCell className="align-top capitalize text-sm">
                              {r.status ?? "—"}
                            </TableCell>
                            <TableCell className="align-top text-sm">
                              {r.receiptNumber ?? r.invoiceNumber ?? "—"}
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        );
                      })
                    : null}
                </Fragment>
              );
            }
            // ---------- Solo row ----------
            const r = item.row;
            const charge =
              r.memberChargeId != null ? pendingChargeById.get(r.memberChargeId) : undefined;
            const canRecordPayment = isChargeRowCollectible(r) && charge != null;
            const canRecordStockOut = isStockOutRowCollectible(r);
            const rowKey = `${r.bucket}-${r.id}`;
            const isCollectible = canRecordPayment || canRecordStockOut;
            const isSelected = selectedRowKeys.has(rowKey);
            return (
              <TableRow key={rowKey} className={isSelected ? "bg-primary/5" : undefined}>
                <TableCell className="align-top">
                  {isCollectible ? (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleRowSelect(rowKey)}
                      className="h-3.5 w-3.5 cursor-pointer accent-primary"
                      aria-label={isSelected ? "Unselect row" : "Select row for bulk pay"}
                    />
                  ) : null}
                </TableCell>
                <TableCell className="whitespace-nowrap align-top">{fmtDate(r.date)}</TableCell>
                <TableCell className="align-top">
                  <div className="font-medium">{r.description}</div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span>{r.category}</span>
                    {r.accountLevel === true && (
                      <Badge variant="outline" className="text-[10px]">
                        Account-level
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="align-top tabular-nums">{formatINR(r.amount)}</TableCell>
                <TableCell className="align-top capitalize">{r.status ?? "—"}</TableCell>
                <TableCell className="align-top">{r.receiptNumber ?? r.invoiceNumber ?? "—"}</TableCell>
                <TableCell className="align-top">
                  {/* Hide per-row CTA once ticked — the floating bar takes
                      over. Keeps the "one action path" invariant that
                      matches the Cart-parent behaviour above. */}
                  {isSelected ? (
                    "—"
                  ) : canRecordPayment ? (
                    <Button size="sm" variant="outline" onClick={() => setPayCharge(charge!)}>
                      Record payment
                    </Button>
                  ) : canRecordStockOut ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setPayStockOut({
                          id: r.id,
                          description: r.description,
                          total: r.amount ?? 0,
                        })
                      }
                    >
                      Record payment
                    </Button>
                  ) : (
                    "—"
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* 2026-07-15 — action bar. Appears when 2+ Pending rows are ticked;
          Collect opens the shared dialog with the selection partitioned
          into primary charge / additional charges / stock-out targets. */}
      {canBulk ? (
        <div className="mt-3 flex items-center justify-between gap-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
          <div className="text-sm">
            <span className="font-medium">{selectedRowKeys.size}</span>{" "}
            <span className="text-muted-foreground">
              pending {selectedRowKeys.size === 1 ? "entry" : "entries"} selected · total{" "}
            </span>
            <span className="font-mono font-semibold tabular-nums">{formatINR(selectedTotal)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setSelectedRowKeys(new Set())}
            >
              Clear
            </Button>
            <Button size="sm" className="h-7 text-xs" onClick={() => setBulkOpen(true)}>
              Collect payment {formatINR(selectedTotal)}
            </Button>
          </div>
        </div>
      ) : null}

      {payCharge && <ChargeRecordPaymentDialog charge={payCharge} onClose={handleDialogClose} />}
      {payStockOut && (
        <ChargeRecordPaymentDialog stockOutTargets={[payStockOut]} onClose={handleStockOutClose} />
      )}
      {bulkOpen && (primaryCharge || selectedStockOutTargets.length > 0) && (
        <ChargeRecordPaymentDialog
          {...(primaryCharge ? { charge: primaryCharge } : {})}
          additionalCharges={additionalCharges}
          stockOutTargets={selectedStockOutTargets}
          onClose={handleBulkClose}
        />
      )}
      {cartPayTarget && (cartPayTarget.primary || cartPayTarget.stockOutTargets.length > 0) && (
        <ChargeRecordPaymentDialog
          {...(cartPayTarget.primary ? { charge: cartPayTarget.primary } : {})}
          additionalCharges={cartPayTarget.additional}
          stockOutTargets={cartPayTarget.stockOutTargets}
          onClose={() => {
            setCartPayTarget(null);
            queryClient.invalidateQueries({ queryKey: getGetContactTransactionsQueryKey(contactId) });
            queryClient.invalidateQueries({ queryKey: getListMemberChargesQueryKey(pendingParams) });
          }}
        />
      )}
    </>
  );
}
